<script setup lang="ts">
import { computed, ref } from 'vue';
import * as Sentry from '@sentry/vue';
import { ZButton } from '@zoomcatalog/core-ui';
import { useAuthStore } from '@/composable/store-utils/useAuthStore';
import {
  isUserCompany,
  useCompanyStore,
} from '@/composable/store-utils/useCompanyStore';
import { useProjectStore } from '@/composable/store-utils/useProjectStore';
import { useAuthService } from '@/service/request-handlers/useAuthService';
import { useCloneTemplate } from '@/service/request-handlers/useCloneTemplate';
import { settings } from '@/settings/DynamicSettings';
import { storageInstances } from '@/utils/storageInstances';

const locale = {
  SIGN_IN_TO_USE_THIS_TEMPLATE:
    'Sign in or create an account to customize this design as your own.',
  CUSTOMIZE_THIS_DESIGN_AS_YOUR_OWN: 'Customize this design as your own',
  MAKE_YOUR_OWN: 'Customize Design',
  SIGN_IN_OR_SIGN_UP: 'Sign in or Sign up',
};

const isLoading = ref(false);
const authService = useAuthService();
const authStore = useAuthStore();
const projectStore = useProjectStore();
const companyStore = useCompanyStore();
const cloneTemplateService = useCloneTemplate();
const rendererKey = computed(() =>
  authStore.isAuthenticated.value ? 'hasAccount' : 'noAccount'
);

function getTokenArgs() {
  if (!companyStore.selectedCompany.value?.company_id)
    throw new Error('No company id found');

  if (isUserCompany(companyStore.selectedCompany.value)) {
    return [companyStore.selectedCompany.value.company_id];
  }

  return [
    projectStore.projectData.value.company_id,
    companyStore.selectedCompany.value.company_id,
  ];
}

const renderer = {
  hasAccount: {
    title: locale.CUSTOMIZE_THIS_DESIGN_AS_YOUR_OWN,
    buttonText: locale.MAKE_YOUR_OWN,
    buttonDisabled: computed(() => !companyStore.selectedCompany.value),
    buttonAction: async () => {
      isLoading.value = true;

      try {
        if (!companyStore.selectedCompany.value) return;
        const sessionId = storageInstances.sessionId.get();
        if (!sessionId) throw new Error('No session id found');

        const tokenArgs = getTokenArgs();
        await authStore.tokenExchange(sessionId, ...tokenArgs);
        const setupWizard = {
          setupWizard: {
            started: true,
            completed: false,
            step: 1,
          },
        };
        const cloneTemplateResponse = await cloneTemplateService.cloneTemplate(
          projectStore.projectData.value.short_id,
          {
            name: projectStore.projectData.value.name,
            settings: {
              ...projectStore.projectData.value.settings,
              ...setupWizard,
            },
          }
        );

        if (!companyStore.selectedCompany.value?.slug)
          throw new Error('No company slug found');

        const signInResponse = await authService.canvasSignInUrl(
          `${settings.QC_UI_URL(companyStore.company.value?.slug ?? '')}/${
            cloneTemplateResponse.data.short_id
          }`
        );
        window.location.href = signInResponse.url;
      } catch (err) {
        isLoading.value = false;
        Sentry.captureException(err);
      }
    },
  },
  noAccount: {
    title: locale.SIGN_IN_TO_USE_THIS_TEMPLATE,
    buttonText: locale.SIGN_IN_OR_SIGN_UP,
    buttonDisabled: computed(() => false),
    buttonAction: () => authService.openPreSignInForm(),
  },
};
</script>

<template>
  <div class="create-your-own">
    {{ renderer[rendererKey].title }}
    <ZButton
      color="primary"
      size="sm"
      :disabled="renderer[rendererKey].buttonDisabled.value"
      :pending="isLoading"
      @click="renderer[rendererKey].buttonAction"
    >
      {{ renderer[rendererKey].buttonText }}
    </ZButton>
  </div>
</template>

<style lang="scss" scoped>
.create-your-own {
  @apply flex justify-between items-center gap-4;
  @apply text-longform-sm font-medium;
  button {
    @apply shrink-0;
  }
}
</style>
